import React from "react";
import { Button } from "../../ui/Button";
import { type BackupEntry } from "../../../services/BackupService";

interface BackupsListProps {
  backups: BackupEntry[];
  onRestoreBackup: (id: string) => void;
  onDownloadBackup: (backup: BackupEntry) => void;
  onDeleteBackup: (id: string) => void;
}

export const BackupsList: React.FC<BackupsListProps> = ({
  backups,
  onRestoreBackup,
  onDownloadBackup,
  onDeleteBackup,
}) => {
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <h3 className="font-medium mb-2">Available Backups</h3>

      {backups.length === 0 ? (
        <p className="text-sm text-gray-500">No backups available</p>
      ) : (
        <div className="border rounded overflow-auto max-h-[300px]">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {backups.map((backup) => (
                <tr key={backup.id}>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    {formatTimestamp(backup.timestamp)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    {formatSize(backup.size)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onRestoreBackup(backup.id)}
                      >
                        Restore
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onDownloadBackup(backup)}
                      >
                        Download
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => onDeleteBackup(backup.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
